import type { CashLocationType } from './cash-locations.dto.js';
import type { CashDenominationRowDto } from './cash-denominations.dto.js';

export const cashDenominationInventoryCheckpointInventoryStatusValues = [
  'known',
  'unavailable',
] as const;
export type CashDenominationInventoryCheckpointInventoryStatus =
  (typeof cashDenominationInventoryCheckpointInventoryStatusValues)[number];

export const cashDenominationInventoryCheckpointApplicationStatusValues = [
  'accepted',
  'ignoredOlder',
  'ignoredEqualAmbiguous',
] as const;
export type CashDenominationInventoryCheckpointApplicationStatus =
  (typeof cashDenominationInventoryCheckpointApplicationStatusValues)[number];

export const cashDenominationInventoryCheckpointOriginValues = [
  'desktopSync',
  'cloudTreasuryCount',
] as const;
export type CashDenominationInventoryCheckpointOrigin =
  (typeof cashDenominationInventoryCheckpointOriginValues)[number];

export const cashDenominationInventoryCheckpointSourceValues = [
  'cleanCount',
  'openingCount',
  'closingCount',
  'forceClosingCount',
  'treasuryCount',
  'purchasePayout',
  'purchaseCancellation',
  'transfer',
  'funding',
  'vaultIntake',
  'expense',
  'adjustment',
  'unknown',
] as const;
export type CashDenominationInventoryCheckpointSource =
  (typeof cashDenominationInventoryCheckpointSourceValues)[number];

export const cashDenominationInventoryCheckpointSourceEventTypeValues = [
  'treasuryCount',
  'cashSessionOpeningCount',
  'cashSessionClosingCount',
  'cashSessionForceClosingCount',
  'cashMovement',
  'cashTransfer',
  'cashExpense',
  'cashVarianceAction',
  'cashDenominationCorrection',
  'desktopCurrentStatePublication',
] as const;
export type CashDenominationInventoryCheckpointSourceEventType =
  (typeof cashDenominationInventoryCheckpointSourceEventTypeValues)[number];

export const cashDenominationInventoryCheckpointSourceEventSideValues = [
  'single',
  'source',
  'destination',
] as const;
export type CashDenominationInventoryCheckpointSourceEventSide =
  (typeof cashDenominationInventoryCheckpointSourceEventSideValues)[number];

export const cashDenominationInventoryCheckpointReadStatusValues = [
  'current',
  'stale',
  'unavailable',
  'pendingSync',
] as const;
export type CashDenominationInventoryCheckpointReadStatus =
  (typeof cashDenominationInventoryCheckpointReadStatusValues)[number];

export const cashDenominationInventoryCheckpointStatusReasonValues = [
  'current',
  'noCheckpoint',
  'latestUnavailable',
  'invalidCheckpointRows',
  'ledgerBalanceMismatch',
  'ledgerMovementCountMismatch',
  'ledgerLastMovementAtMismatch',
  'ledgerLastMovementUpdatedAtMismatch',
] as const;
export type CashDenominationInventoryCheckpointStatusReason =
  (typeof cashDenominationInventoryCheckpointStatusReasonValues)[number];

export interface CashDenominationInventoryCheckpointDto {
  id: string;
  companyId: string;
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
  cashLocationId: string;
  cashLocationType: CashLocationType;
  inventoryStatus: CashDenominationInventoryCheckpointInventoryStatus;
  applicationStatus: CashDenominationInventoryCheckpointApplicationStatus;
  origin: CashDenominationInventoryCheckpointOrigin;
  source: CashDenominationInventoryCheckpointSource;
  sourceAuthorityRank: number;
  sourceEventType: CashDenominationInventoryCheckpointSourceEventType;
  sourceEventId: string;
  sourceEventSide: CashDenominationInventoryCheckpointSourceEventSide;
  sourceEventOccurredAt: string;
  asOf: string;
  publishedAt: string;
  lastSyncedAt: string | null;
  rows: CashDenominationRowDto[] | null;
  totalMinor: number | null;
  ledgerBalanceMinor: number;
  ledgerMovementCount: number | null;
  ledgerLastMovementAt: string | null;
  ledgerLastMovementUpdatedAt: string | null;
  expectedLedgerBalanceMinor: number | null;
  differenceMinor: number | null;
  cashSessionId: string | null;
  lastMovementId: string | null;
  sourceSnapshotId: string | null;
  sourceBaselineId: string | null;
  payloadHash: string;
  createdAt: string;
  updatedAt: string;
}

export interface CashDenominationInventoryReadDto {
  companyId: string;
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
  cashLocationId: string;
  cashLocationType: CashLocationType;
  status: CashDenominationInventoryCheckpointReadStatus;
  statusReason: CashDenominationInventoryCheckpointStatusReason;
  currentLedgerBalanceMinor: number;
  currentLedgerMovementCount: number | null;
  currentLedgerLastMovementAt: string | null;
  currentLedgerLastMovementUpdatedAt: string | null;
  latestCheckpoint: CashDenominationInventoryCheckpointDto | null;
  lastKnownCheckpoint: CashDenominationInventoryCheckpointDto | null;
}

export interface GetCashDenominationInventoryCheckpointRequest {
  companyId: string;
  cashLocationId: string;
}

export interface GetCashDenominationInventoryCheckpointResponse {
  inventory: CashDenominationInventoryReadDto;
}
